import { useMemo } from 'react';
import { useVideoDetail } from '../../api/centerstageQueries';
import { useAdminNarrowViewport } from '../../admin-ui';
import { CenterstageVideoDialog, type CenterstageVideoSummary } from '../centerstage/CenterstageVideoDialog';
import { PICKER_COPY } from './authorWithAiCopy';
import { createVideoPickerStyles } from './videoPickerStyles';

/**
 * The Centerstage page's video dialog, shared by the video picker and the
 * inline scenario list, without the concept links and with "Close" and "Use
 * this one" in place of "Build a lesson around this video" (Sara,
 * 2026-11-01). Any video can be used, with or without captions.
 */

/** What the preview shows until the video's detail loads. */
export type PreviewVideo = CenterstageVideoSummary;

export interface VideoPreviewProps {
  /** The video to preview; null keeps the dialog closed. */
  video: PreviewVideo | null;
  busy?: boolean;
  onClose: () => void;
  onUse: (videoId: string) => void;
}

export function VideoPreview({ video, busy = false, onClose, onUse }: VideoPreviewProps) {
  const narrow = useAdminNarrowViewport();
  const styles = useMemo(() => createVideoPickerStyles({ narrow }), [narrow]);
  // The dialog reads the same cache entry.
  const detailQuery = useVideoDetail(video?.id ?? null);
  const blocked = busy || (detailQuery.isSuccess && detailQuery.data === null);

  return (
    <CenterstageVideoDialog
      styles={styles.centerstage}
      video={video}
      onClose={onClose}
      footer={(
        <>
          <button type="button" style={styles.picker.secondaryButton} onClick={onClose}>{PICKER_COPY.close}</button>
          <button
            type="button"
            style={{ ...styles.picker.primaryButton, ...(blocked ? styles.picker.buttonDisabled : {}) }}
            disabled={blocked}
            onClick={() => { if (video) onUse(video.id); }}
          >
            {PICKER_COPY.useThisOne}
          </button>
        </>
      )}
    />
  );
}
